import React from 'react';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles({
  gridContainer: {
    marginTop: '16px',
    marginBottom: '24px',
  },
  gridTitle: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: '1rem',
    fontWeight: 500,
    marginBottom: '12px',
    color: '#333',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(11, 1fr)',
    gridTemplateRows: 'repeat(11, 1fr)',
    width: '330px',
    height: '330px',
    border: '2px solid #333',
    backgroundColor: '#333',
  },
  cell: {
    backgroundColor: '#f9f9f9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#333',
    textTransform: 'uppercase',
    border: '1px solid #ccc',
  },
  headerCell: {
    backgroundColor: '#e0e0e0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: 'bold',
    color: '#666',
    border: '1px solid #ccc',
  },
  cornerCell: {
    backgroundColor: '#d0d0d0',
    fontSize: '9px',
    color: '#999',
    border: '1px solid #ccc',
  },
  filledCell: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    border: '1px solid #ccc',
  },
  conflictCell: {
    backgroundColor: '#ffebee',
    color: '#d32f2f',
    border: '1px solid #ccc',
  },
  legendContainer: {
    marginTop: '12px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
  },
  legendColor: {
    width: '12px',
    height: '12px',
    borderRadius: '2px',
  },
  instructionText: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '8px',
    fontStyle: 'italic',
  },
});

const WORD_COLORS = [
  '#e3f2fd',
  '#e8f5e8',
  '#fff3e0',
  '#f3e5f5',
  '#e0f2f1',
  '#fce4ec',
  '#e1f5fe',
  '#f9fbe7',
  '#fff8e1',
  '#ede7f6',
];

const CrosswordGridPreview = ({ items = [] }) => {
  const classes = useStyles();

  // Initialize 10x10 grid
  const initializeGrid = () =>
    Array(10)
      .fill(null)
      .map(() => Array(10).fill({ letter: '', wordIndex: -1, conflict: false }));

  // Calculate word positions and populate grid
  const populateGrid = () => {
    const grid = initializeGrid();
    const conflicts = new Set();

    items.forEach((item, wordIndex) => {
      // Check if item exists and has required properties
      if (!item || !item.word || typeof item.word !== 'string' || !item.word.trim()) return;
      if (item.startRow === undefined || item.startRow === null || item.startRow === '') return;
      if (item.startCol === undefined || item.startCol === null || item.startCol === '') return;

      const word = item.word.toUpperCase();
      // Form inputs are 1-10 (user-friendly), convert to 0-9 for internal processing
      const startRow = (parseInt(item.startRow, 10) || 1) - 1;
      const startCol = (parseInt(item.startCol, 10) || 1) - 1;
      const direction = item.direction || 'horizontal';

      // Additional validation - ensure word is not empty after trimming
      if (word.length === 0) return;

      // Calculate positions for each letter
      for (let i = 0; i < word.length; i += 1) {
        let row = startRow;
        let col = startCol;

        switch (direction) {
          case 'horizontal':
            col += i;
            break;
          case 'vertical':
            row += i;
            break;
          default:
            col += i; // fallback to horizontal
        }

        // Check bounds
        if (row >= 0 && row < 10 && col >= 0 && col < 10) {
          const currentCell = grid[row][col];

          // Check for conflicts (different letters in same position)
          if (currentCell.letter && currentCell.letter !== word[i]) {
            conflicts.add(`${row}-${col}`);
            grid[row][col] = {
              letter: word[i],
              wordIndex,
              conflict: true,
            };
          } else {
            grid[row][col] = {
              letter: word[i],
              wordIndex: currentCell.letter ? currentCell.wordIndex : wordIndex,
              conflict: conflicts.has(`${row}-${col}`),
            };
          }
        }
      }
    });

    return grid;
  };

  const grid = populateGrid();

  // Generate legend for words - use same validation as populateGrid
  const validWords = items.filter((item) => item && item.word && typeof item.word === 'string' && item.word.trim());

  // Create the full 11x11 grid with headers
  const renderGrid = () => {
    const gridElements = [];

    // Row 0: Header row with column numbers (1-10)
    for (let col = 0; col < 11; col += 1) {
      if (col === 0) {
        // Corner cell with instruction
        gridElements.push(
          <div
            key="corner"
            className={`${classes.headerCell} ${classes.cornerCell}`}
            title="Grid shows 1-10 for easy reference"
          >
            R/C
          </div>,
        );
      } else {
        // Column headers (1-10) - user-friendly numbering
        gridElements.push(
          <div key={`col-header-${col}`} className={classes.headerCell}>
            {col}
          </div>,
        );
      }
    }

    // Rows 1-10: Row headers + grid cells
    for (let row = 0; row < 10; row += 1) {
      // Row header (1-10) - user-friendly numbering
      gridElements.push(
        <div key={`row-header-${row + 1}`} className={classes.headerCell}>
          {row + 1}
        </div>,
      );

      // Grid cells for this row
      for (let col = 0; col < 10; col += 1) {
        const cell = grid[row][col];

        let cellClassName = classes.cell;
        if (cell.letter) {
          cellClassName += cell.conflict ? ` ${classes.conflictCell}` : ` ${classes.filledCell}`;
        }

        gridElements.push(
          <div
            key={`${row}-${col}`}
            className={cellClassName}
            style={{
              backgroundColor:
                cell.letter && !cell.conflict ? WORD_COLORS[cell.wordIndex % WORD_COLORS.length] : undefined,
            }}
          >
            {cell.letter}
          </div>,
        );
      }
    }

    return gridElements;
  };

  return (
    <div className={classes.gridContainer}>
      <div className={classes.gridTitle}>Crossword Grid Preview (10x10)</div>
      <div className={classes.instructionText}>
        💡 <strong>Tip:</strong> Grid shows 1-10 for easy reference. Form inputs use 1-10.
      </div>
      <div className={classes.grid}>{renderGrid()}</div>
      {validWords.length > 0 && (
        <div className={classes.legendContainer}>
          {validWords.map((word, index) => (
            <div key={index} className={classes.legendItem}>
              <div
                className={classes.legendColor}
                style={{ backgroundColor: WORD_COLORS[index % WORD_COLORS.length] }}
              />
              <span>{word.word}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CrosswordGridPreview;
